import { Button, Table } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencilPlus } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { Title } from '../../components/Title';
import { useFilter } from '../../hooks/useFilter';
import { usePagination } from '../../hooks/usePagination';
import { whiteIconButtonStyle } from '../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { queryWithFilterPageAndSearch } from '../../util/api/queryWithFilterPageAndSearch';
import { NewPosMessageModal } from './components/NewPosMessageModal';
import { PosMessageRow } from './components/PosMessageRow';
import type { PosMessageDto } from './types/PosMessageDto';
import { KeyedRow } from '../../components/KeyedRow';

export function PosMessages() {
	const { organisation } = useParams();
	const pagination = usePagination();
	const filter = useFilter();
	const [searchQuery, setSearchQuery] = useState('');

	const posMessagesCountQuery = queryWithFilterPageAndSearch<number>(
		`${organisation}/pos-messages/count`,
		filter.value,
		pagination.value,
		searchQuery,
	);

	const posMessagesQuery = queryWithFilterPageAndSearch<PosMessageDto[]>(
		`${organisation}/pos-messages/all`,
		filter.value,
		pagination.value,
		searchQuery,
	);

	const [showNewModal, { open: openNewModal, close: closeNewModal }] =
		useDisclosure(false);

	const refreshListCallback = useCallback(() => {
		posMessagesQuery.remove();
		posMessagesCountQuery.remove();

		posMessagesQuery.refetch();
		posMessagesCountQuery.refetch();
	}, []);

	return (
		<>
			<NewPosMessageModal
				close={closeNewModal}
				opened={showNewModal}
				triggerListUpdate={() => refreshListCallback()}
			/>
			<PageHeader
				title={'POS Messages'}
				filter={filter}
				rightContent={
					<Button {...whiteIconButtonStyle} onClick={() => openNewModal()}>
						<IconPencilPlus {...defaultIconStyle} />
					</Button>
				}
			/>
			<DataTable
				pagination={pagination}
				totalItems={posMessagesCountQuery.data?.value}
				isRowsLoading={posMessagesQuery.isLoading}
				isPaginationLoading={posMessagesCountQuery.isLoading}
				rows={pagination.value.pageSize}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Title label={'Start Date'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Start Time'} />
						</Table.Th>
						<Table.Th>
							<Title label={'End Date'} />
						</Table.Th>
						<Table.Th>
							<Title label={'End Time'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Branch'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Till'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Value'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Quantity'} />
						</Table.Th>
						<Table.Th>
							<Title label={'SKUID'} />
						</Table.Th>
						<Table.Th>
							<Title label={'View'} />
						</Table.Th>
						<Table.Th />
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{posMessagesQuery.data?.value.map((posMessage: PosMessageDto) => (
						<PosMessageRow
							initialPosMessage={posMessage}
							updateList={refreshListCallback}
						/>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}
